import React from "react";
import type { UALEvent } from "../types";
import ChangeSourceBadge from "./ChangeSourceBadge";
import ChangeCategoryBadge from "./ChangeCategoryBadge";
import DetailModal from "./DetailModal";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

interface EventDetailModalProps {
  event: UALEvent | null;
  onClose: () => void;
}

type ParsedParams = Record<string, string | number | boolean | null | Record<string, unknown>>;

function parseParameters(raw?: string): ParsedParams | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const result: ParsedParams = {};
      for (const item of parsed) {
        if (item && typeof item === "object" && "Name" in item) {
          const rec = item as Record<string, unknown>;
          result[String(rec.Name)] = rec.Value as string | number | boolean | null;
        }
      }
      return result;
    }
    if (typeof parsed === "object" && parsed !== null) {
      return parsed as ParsedParams;
    }
    return { raw: String(parsed) };
  } catch {
    return { raw: raw ?? "" };
  }
}

function renderParamValue(val: unknown): React.ReactNode {
  if (val === null || val === undefined) return <span className="text-gray-400">null</span>;
  if (typeof val === "object") {
    // Expand one level
    return (
      <div className="pl-3 border-l border-gray-200 space-y-0.5">
        {Object.entries(val as Record<string, unknown>).map(([k, v]) => (
          <div key={k} className="flex gap-1 text-xs">
            <span className="text-gray-500 font-medium">{k}:</span>
            <span className="text-gray-700 break-all">{String(v)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-gray-700 break-all">{String(val)}</span>;
}

export default function EventDetailModal({ event, onClose }: EventDetailModalProps) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  if (!event) return null;

  const params = parseParameters(event.parameters);

  const keyFields: Array<{ label: string; value: React.ReactNode }> = [
    { label: "Record ID", value: event.recordId || "—" },
    { label: "Date / Time", value: formatDate(event.creationTime) },
    { label: "User", value: event.userId || "—" },
    { label: "Workload", value: event.workload || "—" },
    { label: "Object ID", value: event.objectId || "—" },
    { label: "Client IP", value: event.clientIp || "—" },
    { label: "Result Status", value: event.resultStatus || "—" },
    { label: "Category", value: event.changeCategory || "—" },
  ];

  return (
    <DetailModal
      title={event.operation}
      width="md"
      onClose={onClose}
      headerExtra={
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <ChangeSourceBadge changeSource={event.workload} />
          <ChangeCategoryBadge changeCategory={event.changeCategory} />
        </div>
      }
    >
      <div className="space-y-5">
        {/* Key fields — two column grid */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Event Details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {keyFields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
                <dd className="mt-0.5 text-sm text-gray-900 break-all">{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Raw Parameters */}
        {params && Object.keys(params).length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Raw Parameters</h3>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide w-1/3">Key</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(params).map(([k, v]) => (
                    <tr key={k} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-600 align-top">{k}</td>
                      <td className="px-4 py-2 align-top">{renderParamValue(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(!params || Object.keys(params).length === 0) && (
          <p className="text-sm text-gray-400">No parameters available for this event.</p>
        )}
      </div>
    </DetailModal>
  );
}
